/**
 * The frame every converted board is drawn in: one status line above, then the play surface — the
 * era's colours and slow backdrop, a 320 × 320 SVG in board units, the glow of a move that counts
 * and the shake of a mistake — and the one in-board button style.
 */
import { createContext, useContext, useId, useRef } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { ERA_SKIN, EraBackdrop } from '../../art.js';
import type { Era } from '../../art.js';
import type { BoardInput, Pt } from './types.js';
import type { Fx } from './round.js';
import { BAD, BOARD, INK, OK, RADIUS, STROKE, TYPE, WARN, alpha, mono } from './tokens.js';

/** An id for SVG gradients and clips, safe inside `url(#…)`: unique per board, so a demo beside a board never shares one. */
export function useSvgId(): string {
  return `b${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

/** Drawn over the board by whoever shows it (the demo's ghost finger), in board units. */
export const FrameOverlay = createContext<ReactNode>(null);

export type Tone = 'play' | 'ok' | 'bad' | 'warn';

const toneColour = (tone: Tone, accent: string) => (tone === 'ok' ? OK : tone === 'bad' ? BAD : tone === 'warn' ? WARN : accent);

export interface Status {
  /** What is being counted or said, in capitals: "GATES", "✓ THROUGH", "TOO FAST". */
  text: string;
  /** Shown after the text as "2 / 4". */
  count?: readonly [n: number, of: number] | undefined;
  /** A short hint at the right: "TAP TO FLY". */
  hint?: string | undefined;
  /** play (the accent) while it goes on, ok once done, bad after a mistake, warn when careful. */
  tone?: Tone | undefined;
}

/**
 * The status line's hint goes on a narrow board (the demo on a short screen) rather than squeeze
 * the count: a container query, set once for the page.
 */
const KIT_CSS = `.cc-status { container-type: inline-size; }
@container (max-width: 300px) { .cc-status-hint { display: none; } }`;
if (typeof document !== 'undefined' && !document.getElementById('cc-kit-css')) {
  const style = document.createElement('style');
  style.id = 'cc-kit-css';
  style.textContent = KIT_CSS;
  document.head.append(style);
}

/** The one status line above every board: "GATES 2 / 4" on the left, a hint on the right. */
export function StatusLine({ status, accent }: { status: Status; accent: string }) {
  const cut: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div className="cc-status" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, minHeight: 22, fontFamily: mono }}>
      <span style={{ ...cut, flex: '0 0 auto', fontSize: TYPE.status, fontWeight: 700, letterSpacing: 1.4, color: toneColour(status.tone ?? 'play', accent) }}>
        {status.text}
        {status.count && ` ${status.count[0]} / ${status.count[1]}`}
      </span>
      {status.hint && <span className="cc-status-hint" style={{ ...cut, flex: '0 100 auto', minWidth: 0, fontSize: TYPE.hint, letterSpacing: 1, color: INK.dim }}>{status.hint}</span>}
    </div>
  );
}

/** Where a touch lands on the board, in board units, however the board is scaled on screen. */
function toBoard(el: Element, e: { clientX: number; clientY: number }): Pt {
  const box = el.getBoundingClientRect();
  return { x: ((e.clientX - box.left) * BOARD.w) / (box.width || 1), y: ((e.clientY - box.top) * BOARD.h) / (box.height || 1) };
}

/**
 * A board: the status line, then the play surface with the board's SVG content (in board units)
 * and its feedback. Touches reach `input` in board units; without `input` (a demo, the TV) the
 * board is a picture.
 */
export function BoardFrame({
  era,
  accent,
  status,
  fx,
  input,
  children,
  under,
}: {
  era: Era;
  accent: string;
  status: Status;
  fx?: Fx | undefined;
  input?: BoardInput | undefined;
  /** SVG content, in board units (0–320 across and down). */
  children: ReactNode;
  /** Drawn under the board's content and over the backdrop, as HTML (rarely needed). */
  under?: ReactNode;
}) {
  const skin = ERA_SKIN[era];
  const overlay = useContext(FrameOverlay);
  const [hit, miss] = fx ?? [0, 0];
  // A mistake shakes the surface side to side, a few times, dying away; no flash.
  const shake = miss > 0 ? Math.sin(miss * Math.PI * 6) * 6 * miss : 0;
  const holding = useRef(false);
  const handlers = input
    ? {
        onPointerDown: (e: ReactPointerEvent<SVGSVGElement>) => {
          if (!input.down && !input.move && !input.up) return;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          holding.current = true;
          input.down?.(toBoard(e.currentTarget, e));
        },
        onPointerMove: (e: ReactPointerEvent<SVGSVGElement>) => {
          if (holding.current) input.move?.(toBoard(e.currentTarget, e));
        },
        onPointerUp: () => {
          if (!holding.current) return;
          holding.current = false;
          input.up?.();
        },
        onPointerCancel: () => {
          if (!holding.current) return;
          holding.current = false;
          input.up?.();
        },
      }
    : {};
  return (
    <div>
      <StatusLine status={status} accent={accent} />
      <div style={{ height: 8 }} />
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${BOARD.w} / ${BOARD.h}`,
          borderRadius: RADIUS.board,
          overflow: 'hidden',
          background: `radial-gradient(120% 90% at 50% 0%, ${alpha(skin.ink, 0.12)}, transparent 60%), linear-gradient(180deg, ${skin.bg}, #040609)`,
          border: `1px solid ${miss > 0.15 ? BAD : alpha(accent, 0.27)}`,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,.03), inset 0 -40px 60px rgba(0,0,0,.45)${hit > 0 ? `, inset 0 0 ${Math.round(46 * hit)}px ${alpha(accent, 0.45 * hit)}` : ''}`,
          transform: shake ? `translateX(${shake.toFixed(1)}px)` : undefined,
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <EraBackdrop era={era} opacity={0.35} />
        {under}
        <svg
          viewBox={`0 0 ${BOARD.w} ${BOARD.h}`}
          role={input ? 'application' : undefined}
          aria-label={input?.label}
          aria-hidden={input ? undefined : true}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor: input ? 'pointer' : undefined }}
          {...handlers}
        >
          {children}
          {miss > 0.15 && <rect x="1" y="1" width={BOARD.w - 2} height={BOARD.h - 2} rx={RADIUS.board - 1} fill="none" stroke={BAD} strokeWidth={STROKE.bold} opacity={miss} />}
          {overlay}
        </svg>
      </div>
    </div>
  );
}

export type ButtonTone = 'plain' | 'primary' | 'ok' | 'bad';

/**
 * The one in-board button, drawn in board units: at least 44 tall, a rounded box, a word or a
 * picture in the middle. With `onPress` (live play) it takes a touch; without it, it is a picture
 * of a button (a demo, the TV) and `down` shows it pressed.
 */
export function BoardButton({
  x,
  y,
  w,
  h = 48,
  label,
  accent,
  tone = 'plain',
  down = false,
  dim = false,
  onPress,
  children,
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
  /** The word on it, and what a screen reader says. */
  label: string;
  accent: string;
  tone?: ButtonTone;
  /** Shown pressed. */
  down?: boolean;
  /** Not to be pressed now. */
  dim?: boolean;
  onPress?: (() => void) | undefined;
  /** A picture in place of the word, centred on (0, 0). */
  children?: ReactNode;
}) {
  const fill =
    tone === 'primary' ? accent : tone === 'ok' ? alpha(OK, 0.2) : tone === 'bad' ? alpha(BAD, 0.18) : down ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.06)';
  const edge = tone === 'primary' ? accent : tone === 'ok' ? OK : tone === 'bad' ? BAD : alpha(accent, down ? 0.9 : 0.45);
  const ink = tone === 'primary' ? INK.onFill : tone === 'ok' ? OK : tone === 'bad' ? BAD : INK.text;
  return (
    <g
      transform={`translate(${x} ${y + (down ? 1.5 : 0)})`}
      opacity={dim ? 0.45 : 1}
      role={onPress ? 'button' : undefined}
      aria-label={onPress ? label : undefined}
      style={onPress ? { cursor: 'pointer' } : undefined}
      onPointerDown={
        onPress
          ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              onPress();
            }
          : undefined
      }
    >
      <rect width={w} height={Math.max(44, h)} rx={RADIUS.button} fill={fill} stroke={edge} strokeWidth={STROKE.line} />
      {children ? (
        <g transform={`translate(${w / 2} ${Math.max(44, h) / 2})`}>{children}</g>
      ) : (
        <text x={w / 2} y={Math.max(44, h) / 2 + TYPE.button * 0.36} textAnchor="middle" fontFamily={mono} fontSize={TYPE.button} fontWeight="700" letterSpacing="1.5" fill={ink}>
          {label}
        </text>
      )}
    </g>
  );
}
